import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { v4 as uuidv4 } from "uuid";
import { type IUser } from "src/components/shared/UserList/UserList.types";
import {
  type ICheckList,
  type IFile,
  type IHistoryItem,
  type ILogbooksForSelect,
  type ISection,
} from "src/interfaces";
import { type IYachtData } from "src/pages/Reports/Report/Report.types";

import { type UserSelectItem } from "../../generator/_Modals/AuthorizationModal/AuthorizationModal";
import { clearChecklistsFromFlags } from "../../generator/_Modals/CheckListsModal/checklists.utils";
import {
  isOnline,
  mapGeneratorLogbooks,
  mapLogbookEntriesForSelect,
  resolveEntryId,
} from "../../utils";
import { convertToUUIDKeys } from "../utils/convertToUUIDKeys";
import { type INMEASection } from "../../generator/_Modals/NMEAAutomationModal/NMEAAutomationModal.utils";
import { sortRowsByDateTime } from "../../generator/utils/sortByDateTime";

interface Meta {
  currentMeta: string;
  currentMetaValue: string;
}

interface Cabin {
  label: string;
  value: string;
}

interface ExtraLink {
  link: string;
  linkName: string;
}

export enum ESaveEntryReason {
  deleteRow = "removedRow",
  restoreRow = "restoredRow",
  removedBlankRows = "removedBlankRows",
  none = "none",
}
export type TDeisolatedKeys = "1700" | "2030";

interface IGeneratorState {
  isInitialInsert: boolean;
  isInitiallyOpened: boolean;
  refetchIterator: number;
  locked24: boolean;
  files: IFile[];
  history: IHistoryItem[];
  loading: boolean;
  success: boolean | null;
  error: string | null;
  users: IUser[];
  logbooks: ILogbooksForSelect[];
  lbentry: Ilbentry;
  entries: any[];
  entryDataToSave: any;
  collectionId: string;
  timeZone: string | null;
  latestEntry: any;
  exclusives: any;
  meta: Meta;
  yachtData: IYachtData | null;
  yachtName: string;
  cabins: Cabin[];
  currentSignature: string;
  shipType: string;
  isRestricted: boolean | null;
  showAutoSaveModal: boolean;
  draft: null | any;
  sendDataOnNewEntry: boolean;
  checkListsModalProps: {
    opened: boolean;
    id?: string;
    cid?: string;
    selectedIndex?: number | null;
    summaryChecklists?: any[];
  };
  features: Record<string, any>;
  automationIds: Record<string, Record<string, any>>;
  stampsModalProps: {
    opened: boolean;
    stampid?: string;
  };
  offlineChecklistsCopy: ICheckList[];
  clearHorizontalScrolls: boolean;
  saveReason: ESaveEntryReason;
  dynamicRowAdded: number;
  triggerInlineSave: number;
  persistInlineSave: number;
  forceTurnOffInlineSave: number;
  bridgeLogUsers: UserSelectItem[];
  locked24SectionIndex: number;
  triggerTotalsUpdate: number;
  previewAvailable: boolean;
  save24LockAvailable: boolean;
  sortOrder: "asc" | "desc"; // Track sort order for entries
  shared?: string[]; // Array of shared lbids
  previousVoyage?: Record<string, any>; // Previous voyage data from backend
  voyages: Record<number, { start: string | null; end: string | null }>;
  snapshotEntry?: any; // Historical snapshot entry data
  snapshotHeaders?: any[]; // Headers for snapshot display
  snapshotPageSize: number; // Pagination size for snapshot display
  flags?: string; // Comma-delimited feature flags
}

interface Ilbentry {
  generator?: string;
  sections: ISection[];
  logo: string;
  primary: string;
  secondary: string;
  entryText?: string;
  shipsTime: number | null;
  signator: string;
  type: string;
  catSelect: boolean;
  entryType?: string;
  checklists: ICheckList[];
  eventStatus?: string | null;
  signingOverride?: string | null;
  entrydate: string;
  lockingDate?: string;
  excludeExistingEntryPopup?: boolean;
  // deisolatedLastAcknowledgeTime?: string;
  deisolate1700?: boolean;
  deisolateTime1700?: string;
  deisolate2030?: boolean;
  deisolateTime2030?: string;
  continousEntry?: boolean;
  NMEA?: boolean;
  NMEAData?: INMEASection[];
  isBridgeLog?: boolean;
  sortToggleable?: boolean;
  isReadonly: boolean;
  globalCollapseable?: boolean;
  globalCollpaseable?: boolean; // Handle typo in existing data
  prevEntryData?: Record<string, any>;
  unCodeEnabled?: boolean;
  showPDFButton?: boolean;

  addRowsToTop?: boolean;
  showDateTimePerRow?: boolean;
  extraLinks?: ExtraLink[];
}

const initialState: IGeneratorState = {
  isInitialInsert: false,
  isInitiallyOpened: true,
  refetchIterator: 0,
  locked24: false,
  locked24SectionIndex: -1,
  save24LockAvailable: false,
  sortOrder: "asc", // Start with ascending order
  files: [],
  history: [],
  loading: false,
  success: null,
  error: null,
  users: [],
  entries: [],
  logbooks: [],
  timeZone: null,
  entryDataToSave: {
    datecreated: null,
    entrydate: null,
    lbeid: "",
    lbid: "",
    yachtid: "",
    _prename: "",
  },
  collectionId: "",
  lbentry: {
    sections: [],
    logo: "",
    primary: "",
    secondary: "",
    entryText: "",
    shipsTime: null,
    catSelect: false,
    signator: "",
    type: "",
    checklists: [],
    eventStatus: null,
    signingOverride: null,
    lockingDate: "",
    entrydate: "",
    isReadonly: false,
    prevEntryData: {},
    unCodeEnabled: false,
  },
  latestEntry: { entryref: "", id: "", status: "" },
  exclusives: null,
  meta: { currentMeta: "", currentMetaValue: "" },
  yachtData: null,
  yachtName: "",
  cabins: [],
  currentSignature: "",
  shipType: "",
  isRestricted: null,
  showAutoSaveModal: false,
  draft: null,
  sendDataOnNewEntry: false,
  checkListsModalProps: {
    opened: false,
    id: undefined,
    cid: undefined,
  },
  features: {},
  stampsModalProps: {
    opened: false,
  },
  offlineChecklistsCopy: [],
  clearHorizontalScrolls: false,
  saveReason: ESaveEntryReason.none,
  dynamicRowAdded: -1,
  triggerInlineSave: -1,
  persistInlineSave: -1,
  forceTurnOffInlineSave: -1,
  bridgeLogUsers: [],
  triggerTotalsUpdate: -1,
  previewAvailable: false,
  automationIds: {},
  shared: [], // Initialize empty array for shared logbooks
  previousVoyage: undefined,
  voyages: {},
  snapshotEntry: undefined,
  snapshotHeaders: undefined,
  snapshotPageSize: 25,
  flags: undefined,
};

const generatorSlice = createSlice({
  name: "generator",
  initialState,
  reducers: {
    setSendDataOnNewEntry: (state, action: PayloadAction<boolean>) => {
      state.sendDataOnNewEntry = action.payload;
    },
    setIsInitialInsert: (state, action) => {
      state.isInitialInsert = action.payload;
    },
    refetchLogbook: state => {
      state.refetchIterator = state.refetchIterator + 1;
    },
    setSaveReason: (state, action: PayloadAction<ESaveEntryReason>) => {
      state.saveReason = action.payload;
    },
    setTriggerTotalsUpdate: (state, action: PayloadAction<number>) => {
      state.triggerTotalsUpdate = action.payload;
    },
    resetState: () => {
      return initialState;
    },
    toggleSortOrder: state => {
      state.sortOrder = state.sortOrder === "asc" ? "desc" : "asc";
    },
    setBridgeLogUsers: (state, action: PayloadAction<UserSelectItem[]>) => {
      state.bridgeLogUsers = action.payload;
    },
    reorderRows: (
      state,
      action: { payload: { sectionIndex: number; from: number; to: number } },
    ) => {
      const { sectionIndex, from, to } = action.payload;
      if (from === to) return;
      const section = state.lbentry.sections[sectionIndex];
      if (!section?.rows || from < 0 || to < 0) return;
      if (from >= section.rows.length || to >= section.rows.length) return;

      const originalRows = [...section.rows];

      // Remove the item from the original position
      const [movedItem] = section.rows.splice(from, 1);

      // Insert the item at the new position
      section.rows.splice(to, 0, movedItem);

      // Also reorder the savedRows array if it exists (to keep saved states in sync)
      const savedRows = section.savedRows;
      if (savedRows && savedRows.length > 0) {
        const savedByRow = new Map<any, boolean>(
          originalRows.map((row, index) => [row, savedRows[index] ?? false]),
        );

        const reorderedSavedRows = section.rows.map(
          row => savedByRow.get(row) ?? false,
        );

        if (reorderedSavedRows[to] !== undefined) {
          reorderedSavedRows[to] = false;
        }

        section.savedRows = reorderedSavedRows;
      }
    },
    setInitiallyOpen: (state, action) => {
      state.isInitiallyOpened = action.payload;
    },
    setLogbook: (state, action) => {
      state.lbentry.sections = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    setTimeZone: (state, action) => {
      state.timeZone = action.payload;
    },
    changeTimeZone: (state, action) => {
      state.timeZone = action.payload;
      // state.shipTimeChanged = true;
    },
    setExlusives: (state, action) => {
      state.exclusives = action.payload;
    },
    setCurrentMeta: (state, action) => {
      state.meta = action.payload;
    },
    setYachtName: (state, action) => {
      state.yachtName = action.payload;
      // state.yachtData = {
      //   ...(state.yachtData ? { ...state.yachtData } : {}),
      //   yachtname: action.payload,
      // };
    },
    setIsRestricted: (state, action) => {
      state.isRestricted = action.payload;
    },
    setNMEAData: (state, action: PayloadAction<INMEASection[]>) => {
      state.lbentry.NMEAData = action.payload;
    },
    setCurrentSignature: (state, action) => {
      state.currentSignature = action.payload;
    },
    // addNewDynamicRow: (state, action) => {
    //   const { row, sectionIndex, rowIndex } = action.payload;
    //   state.dynamicRowAdded = sectionIndex;
    //   if (typeof rowIndex === "number") {
    //     state.lbentry.sections[sectionIndex].rows[rowIndex] = row;
    //   } else if (Array.isArray(row)) {
    //     state.lbentry.sections[sectionIndex].rows.push(...row);
    //   } else {
    //     state.lbentry.sections[sectionIndex].rows.push(row);
    //   }    // },
    addNewDynamicRow: (
      state,
      action: PayloadAction<{
        row: any;
        sectionIndex: number;
        rowIndex?: number;
        skipSorting?: boolean;
        addToTop?: boolean;
        withTime?: boolean;
      }>,
    ) => {
      const {
        row: cleanRow,
        sectionIndex,
        rowIndex,
        skipSorting,
        addToTop,
        withTime,
      } = action.payload;
      state.dynamicRowAdded = sectionIndex; // This can trigger changes globally if "state" is shallow-copied.

      const addCurrentTimeToCols = (cols: Record<string, any>) => {
        const currentTime = dayjs().format("HH:mm");
        const updatedCols = { ...cols };

        for (const [key, col] of Object.entries(updatedCols)) {
          const colData = col as any;

          if (colData.headerStyle) continue;

          if (
            colData.type === "time" ||
            colData.unit === "24lock" ||
            (colData.name && colData.name.toLowerCase().includes("time"))
          ) {
            if (
              !colData.value ||
              (typeof colData.value === "object" && !colData.value.time)
            ) {
              updatedCols[key] = {
                ...colData,
                value: {
                  ...((typeof colData.value === "object" && colData.value) ||
                    {}),
                  time: currentTime,
                  type: colData.type || "time",
                },
              };
            }
          }
        }

        return updatedCols;
      };

      const addCurrentTimeToRow = (rowData: any) => {
        const updatedRow = { ...rowData };

        if (updatedRow.cols) {
          updatedRow.cols = addCurrentTimeToCols(updatedRow.cols);
        }

        if (updatedRow.subrows?.length) {
          updatedRow.subrows = updatedRow.subrows.map((subrow: any) => ({
            ...subrow,
            cols: subrow.cols ? addCurrentTimeToCols(subrow.cols) : subrow.cols,
          }));
        }

        return updatedRow;
      };

      const row = withTime ? addCurrentTimeToRow(cleanRow) : cleanRow;

      // Performance optimization: use direct mutation with Immer
      const section = state.lbentry.sections[sectionIndex];
      if (!section) return;

      if (typeof rowIndex === "number") {
        section.rows[rowIndex] = row;
      } else if (Array.isArray(cleanRow)) {
        const rows = cleanRow.map(r => addCurrentTimeToRow(r));
        if (addToTop) {
          section.rows.unshift(...rows);

          if (section.grayOldRows) {
            if (!section.savedRows) section.savedRows = [];
            for (let i = 0; i < cleanRow.length; i++) {
              section.savedRows.unshift(false);
            }
          }
        } else {
          section.rows.push(...rows);
          // Initialize savedRows entries for new rows (unsaved by default)
          if (section.grayOldRows) {
            if (!section.savedRows) section.savedRows = [];
            for (let i = 0; i < cleanRow.length; i++) {
              section.savedRows.push(false);
            }
          }
        }
      } else {
        if (addToTop) {
          section.rows.unshift(row);
          if (section.grayOldRows) {
            if (!section.savedRows) section.savedRows = [];
            section.savedRows.unshift(false);
          }
        } else {
          section.rows.push(row);
          // Initialize savedRows entry for new row (unsaved by default)
          if (section.grayOldRows) {
            if (!section.savedRows) section.savedRows = [];
            section.savedRows.push(false);
          }
        }
      }
      state.triggerTotalsUpdate = Math.random();
    },
    setLandscapeSection: (
      state,
      action: PayloadAction<{
        section: ISection;
        sectionIndex: number;
        subSectionIndex: number;
      }>,
    ) => {
      const { section, sectionIndex, subSectionIndex } = action.payload;
      if (!state.lbentry.sections[sectionIndex]?.subSections) return;
      state.lbentry.sections[sectionIndex].subSections[subSectionIndex] =
        section as any;
    },
    setSections: (state, action: PayloadAction<ISection[]>) => {
      state.lbentry.sections = action.payload as any;
    },
    sortSectionsByTime: (state, action) => {
      const sectionIndex = action?.payload;
      if (typeof sectionIndex === "number") {
        // Sort specific section only
        const section = state.lbentry.sections[sectionIndex];
        section.rows = sortRowsByDateTime(
          section.rows,
          state.sortOrder === "asc",
          section.dynamicRows,
        );
        // Reset savedRows when sorting (all rows marked as unsaved after sort)
        if (section.savedRows) {
          section.savedRows = section.rows.map(() => false);
        }
      } else {
        // Sort all sections
        state.lbentry.sections = state.lbentry.sections.map(section => ({
          ...section,
          rows: sortRowsByDateTime(
            section.rows,
            state.sortOrder === "asc",
            section.dynamicRows,
          ),
          // Reset savedRows when sorting (all rows marked as unsaved after sort)
          savedRows: section.savedRows
            ? section.rows.map(() => false)
            : undefined,
        }));
      }
    },
    removeDynamicRow: (
      state,
      action: PayloadAction<{ sectionIndex: number; rowIndex: number }>,
    ) => {
      const {
        payload: { sectionIndex, rowIndex },
      } = action;
      state.triggerTotalsUpdate = Math.random();
      state.lbentry.sections[sectionIndex].rows.splice(rowIndex, 1);

      // Also remove from savedRows if it exists
      const section = state.lbentry.sections[sectionIndex];
      if (section.savedRows && section.savedRows.length > rowIndex) {
        section.savedRows.splice(rowIndex, 1);
      }
    },
    setNewEntryData: (state, action) => {
      const { lbentry, users, yachtData, ...payload } = action.payload;

      // console.log("new entry sections", lbentry.sections);
      const { convertedSections, exclusives } = convertToUUIDKeys(
        lbentry.sections ?? [],
      );

      convertedSections.forEach((section: any) => {
        if (
          (section.showDateTimePerRow || lbentry.showDateTimePerRow) &&
          section.rows &&
          section.rows.length > 0
        ) {
          const firstRow = section.rows[0];
          if (!firstRow.date) {
            dayjs.extend(utc);
            const now = dayjs();
            (firstRow as any).date = now.toISOString();
            (firstRow as any).dateUTC = now.utc().toISOString();
          }
        }
      });

      state.lbentry = {
        ...lbentry,
        checklists: lbentry.checklists ?? [],
        sections: convertedSections,
        isReadonly: lbentry.isReadonly ?? false,
        unCodeEnabled:
          lbentry.unCodeEnabled ?? state.lbentry.unCodeEnabled ?? false,
      };
      state.exclusives = exclusives;

      state.entryDataToSave.datecreated = new Date().toISOString();
      state.entryDataToSave.entrydate = dayjs().format("DD MMM YYYY");
      if (yachtData) state.yachtData = yachtData;
      if (users) state.users = users;

      state.persistInlineSave = -1;
      state.triggerInlineSave = -1;
      state.files = [];
      state.locked24 = false;
      state.locked24SectionIndex = -1;
      state.features = {};
      state.latestEntry = {
        ...state.latestEntry,
        id: "new",
        entryref: "",
      };
    },
    addDynamicSection: (
      state,
      action: PayloadAction<{
        sectionIndex: number;
        section: ISection;
        replace?: boolean;
      }>,
    ) => {
      const { sectionIndex, section, replace } = action.payload;
      const sections = [...state.lbentry.sections];
      const deleteCount = replace ? 1 : 0;
      sections.splice(sectionIndex, deleteCount, section as any);
      state.lbentry.sections = sections;
      state.triggerTotalsUpdate = Math.random();
    },
    removeDynamicSection: (state, action: PayloadAction<number>) => {
      state.lbentry.sections.splice(action.payload, 1);
    },
    setFiles: (state, action) => {
      state.files = action.payload;
    },
    removeFile: (state, action) => {
      state.files = state.files.map(f =>
        f.aid !== action.payload ? f : { ...f, deleted: 1 },
      );
    },
    setShipType: (state, action) => {
      state.shipType = action.payload;
    },
    setShowAutoSaveModal: (state, action) => {
      state.showAutoSaveModal = action.payload;
    },
    setSaveLockAvailable: (state, action) => {
      state.save24LockAvailable = action.payload;
    },
    setLock24: (
      state,
      action: PayloadAction<{ time: any; sectionIdx?: number }>,
    ) => {
      const time = action.payload.time.time;
      if (time && action.payload?.time) {
        const lockingDateStr =
          state.lbentry.lockingDate || state.entryDataToSave.datecreated;

        if (!lockingDateStr) {
          state.locked24 = false;
          state.locked24SectionIndex = -1;
          return;
        }

        // Parse lockingDate as YYYY-MM-DD in local timezone
        const [year, month, day] = lockingDateStr.split("-").map(Number);
        const [hour, minute] = time.split(":").map(Number);

        // Construct the date in local time without timezone shifts
        const dateCreated = new Date(year, month - 1, day, hour, minute, 0, 0);

        // Get the current local time
        const now = new Date();
        // Calculate the difference
        const difference = Math.abs(now.getTime() - dateCreated.getTime());
        const isLocked = difference >= 24 * 60 * 60 * 1000;
        // WARNING: DISABLED locked24 variable !!!!
        //
        // if (isLocked) {
        //   state.locked24SectionIndex = action.payload.sectionIdx ?? -1;
        // }
        // state.locked24 = isLocked;
      } else {
        // state.locked24 = false;
        // state.locked24SectionIndex = -1;
      }
    },
    setEntryDate: (state, action) => {
      state.entryDataToSave.entrydate = action.payload;
    },
    setEntryLockingDate: (state, action) => {
      state.lbentry.lockingDate = action.payload;
    },
    setLatestEntryStatus: (state, action) => {
      const { status, autosave } = action.payload;
      state.latestEntry.status = status;
      state.latestEntry.autosave = autosave;
    },
    setLatestEntryId: (state, action: PayloadAction<string>) => {
      state.latestEntry.id = action.payload;
    },
    setCheckListsModalProps: (
      state,
      action: PayloadAction<{
        opened: boolean;
        id?: string;
        cid?: string;
        selectedIndex?: number | null;
        summaryChecklists?: any[];
      }>,
    ) => {
      state.checkListsModalProps = action.payload;
    },
    setCheckList: (state, action) => {
      const [clearedChecklist] = clearChecklistsFromFlags(
        [action.payload],
        ["changed", "naChanged"],
      );
      const findIndex = state.lbentry.checklists.findIndex(
        el => el.cid === clearedChecklist.cid,
      );
      if (findIndex !== -1) {
        const newChecklists = [...state.lbentry.checklists];
        if (clearedChecklist.suffix == null) {
          clearedChecklist.suffix =
            state.lbentry.checklists[findIndex].suffix ?? 1;
        }
        newChecklists[findIndex] = clearedChecklist;
        state.lbentry.checklists = newChecklists;
      } else {
        if (!action.payload.suffix) clearedChecklist.suffix = 1;
        state.lbentry.checklists.splice(
          clearedChecklist.sort - 1,
          0,
          clearedChecklist,
        );
        state.lbentry.checklists = state.lbentry.checklists.map((el, i) => ({
          ...el,
          sort: i + 1,
        }));
      }
      if (window.api && !isOnline()) {
        state.offlineChecklistsCopy = state.lbentry.checklists;
      }
    },
    setFeature: (
      state,
      action: PayloadAction<{ key: string; feature: any; insert?: boolean }>,
    ) => {
      state.features[action.payload.key] = action.payload.insert
        ? { ...state.features[action.payload.key], ...action.payload.feature }
        : action.payload.feature;
    },

    addAutomationId: (
      state,
      action: PayloadAction<{
        key: string;
        subKey: string | number;
        feature: any;
      }>,
    ) => {
      const { key, feature, subKey } = action.payload;
      let newData = state.automationIds;
      if (!newData[key]) {
        newData[key] = {};
      }
      newData[key] = {
        ...(newData?.[key] ?? {}),
        [subKey]: { ...(newData?.[key]?.[subKey] ?? {}), ...feature },
      };

      state.automationIds = newData;
    },
    setOowFeature: (
      state,
      action: PayloadAction<{ key: string; value: any }>,
    ) => {
      const { key, value } = action.payload;
      if (!state.features.oowCheck) {
        state.features.oowCheck = {};
      }
      (state.features.oowCheck as any)[key] = value;
    },
    setDeisolate: (state, action: PayloadAction<{ key: TDeisolatedKeys }>) => {
      const { key } = action.payload;
      const currentTime = dayjs().format(); // Example: "2025-03-31T17:06:40-04:00" or "2025-03-31T17:06:40+02:00"
      const dateTime = currentTime
        .replace(/([+-]\d{2}:\d{2})$/, "")
        .replace("T", " ");

      state.lbentry[`deisolate${key}`] = true;
      state.lbentry[`deisolateTime${key}`] = dateTime;
    },
    deleteFeature: (state, action: PayloadAction<{ key: string }>) => {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete state.features[action.payload.key];
    },
    clearOfflineChecklistsCopy: state => {
      state.offlineChecklistsCopy = [];
    },
    setCheckLists: (state, action: PayloadAction<ICheckList[]>) => {
      state.lbentry.checklists = action.payload;
    },
    setStampsModalProps: (
      state,
      action: PayloadAction<{ opened: boolean; stampid?: string }>,
    ) => {
      state.stampsModalProps = action.payload;
    },
    toggleHorizontalScrolls: state => {
      state.clearHorizontalScrolls = !state.clearHorizontalScrolls;
    },
    setTriggerInlineSave: (state, action: PayloadAction<number>) => {
      state.triggerInlineSave = action.payload;
    },
    setPersistInlineSave: (state, action: PayloadAction<number>) => {
      state.persistInlineSave = action.payload;
    },
    setEventStatus: (state, action: PayloadAction<string>) => {
      state.lbentry.eventStatus = action.payload;
    },
    setSigningOverride: (
      state,
      action: PayloadAction<string | null | undefined>,
    ) => {
      state.lbentry.signingOverride = action.payload ?? null;
    },
    setForceTurnOffInlineSave: (state, action: PayloadAction<number>) => {
      state.forceTurnOffInlineSave = action.payload;
    },

    setLogbookData: (state, action) => {
      const logbooksForSelect: any[] = mapGeneratorLogbooks(
        action.payload?.data?.content?.data?.logbooks ?? [],
      );
      const entriesForSelect = mapLogbookEntriesForSelect(
        action.payload.data.content.data.entries ?? [],
      );

      const collectionId = action.payload?.data?.content?.data?.latest?.colid;
      const lbentry = action.payload?.data?.content?.data?.latest?.lbentry;
      const latest = action.payload?.data?.content?.data?.latest;

      // Apply override sort options before converting sections
      const overrideSortOptions = {
        "32e48195-2f8d-11ec-9455-d45d6455e22d": { dateCol: 0 },
        "a1ef4059-e85b-11ee-a2b2-00155d46168a": { dateCol: 0 },
        "df8e86d8-645a-11ed-b1a7-d45d6455e22d": { dateCol: 0 },
        "439e9401-f0e9-11ee-b3b1-00155dc3514b": { dateCol: 0 },
        "cd415589-cdba-9980-01d1-2fae9bced3dc": { dateCol: 0 },
        "37011e13-20f9-11f0-bac5-00155d9676ca": { dateCol: 0 },
      };

      if (latest?.lbid && latest.lbid in overrideSortOptions) {
        const override =
          overrideSortOptions[latest.lbid as keyof typeof overrideSortOptions];
        lbentry?.sections?.forEach((section: any) => {
          if (section.rows) {
            section.rows.forEach((row: any) => {
              if (Array.isArray(row) && row.length > override.dateCol) {
                const dateCol = row[override.dateCol];
                if (dateCol?.unit === "date") {
                  dateCol.sortby = true;
                }
              }
            });
          }
        });
      }

      // Now convert sections with the updated sortby properties
      const { convertedSections, exclusives } = convertToUUIDKeys(
        lbentry?.sections ?? [],
      );

      if (!lbentry) return { ...state, saveReason: ESaveEntryReason.none };
      if (!lbentry.firstSaveTime) lbentry.firstSaveTime = "";
      lbentry.eventStatus = latest.eventstatus ?? null;
      lbentry.isBridgeLog =
        action.payload?.data?.content?.data?.latest?.lbentry?.isBridgeLog ??
        false;
      lbentry.sortToggleable =
        action.payload?.data?.content?.data?.latest?.lbentry?.sortToggleable ??
        false;

      const cabins = !action.payload?.data?.content?.data?.yachtdata?.uppercase
        ? action.payload?.data?.content?.data?.yachtdata?.cabins
        : action.payload?.data?.content?.data?.yachtdata?.cabins.map(
            (cabin: string) => cabin.toUpperCase(),
          );
      if (lbentry?.checklists === undefined) lbentry.checklists = [];
      if (
        typeof lbentry.checklists === "object" &&
        !Array.isArray(lbentry.checklists)
      ) {
        const parsed = [];
        for (const key in lbentry.checklists) {
          parsed.push({ ...lbentry.checklists[key], cid: key });
        }
        lbentry.checklists = parsed;
      }
      const checklistsWithSuffixes = lbentry.checklists.map((el: ICheckList) =>
        !el.suffix ? { ...el, suffix: 1 } : el,
      );
      lbentry.checklists = clearChecklistsFromFlags(checklistsWithSuffixes, [
        "changed",
        "naChanged",
      ]);
      // Saving Yacht Data to local storage
      localStorage.setItem(
        "yachtData",
        JSON.stringify({
          ...action.payload.data?.content?.data?.yachtdata,
          yachtname: action.payload?.data?.yachtname,
        }),
      );
      const id = resolveEntryId(latest);
      const files =
        action.payload?.data?.content?.data?.latest?.attachments ?? [];
      const autosave =
        entriesForSelect.find((e: any) => e.value === id)?.autosave ?? 0;
      let locked24 = false;
      if (Object.keys(lbentry).includes("hour24dts")) {
        // disabled locked24 variable!
        // locked24 = lbentry.hour24Locked;
      }

      let checklistsToSet = lbentry.checklists ?? [];
      if (
        window.api &&
        state.offlineChecklistsCopy.length > 0 &&
        isOnline()
      ) {
        checklistsToSet = state.offlineChecklistsCopy;
      }

      return {
        ...state,
        previewAvailable: latest?.preview === 1,
        features: {},
        automationIds: {},
        locked24,
        locked24SectionIndex: -1,
        collectionId,
        files,
        history: action.payload?.data?.content?.data?.history ?? [],
        lbentry: {
          ...lbentry,
          entrydate: latest?.sortdate,
          sections: convertedSections,
          checklists: checklistsToSet,
          isReadonly: action.payload.data.content?.data?.isReadonly ?? false,
          unCodeEnabled:
            action.payload.data.content?.data?.unCodeEnabled ?? false,
        },
        users: action.payload.data.content.data.users,
        success: action.payload.success,
        loading: false,
        logbooks: logbooksForSelect,
        entries: entriesForSelect,
        entryDataToSave: {
          datecreated: latest?.datecreated,
          datecreatedUTC: latest?.datecreatedUTC,
          entrydate: null,
          latest: resolveEntryId(latest),
          lbid: latest?.lbid,
          yachtid: latest?.yachtid,
          _prename: latest?.lbname,
        },
        latestEntry: {
          entryref: latest?.enum,
          lbName: latest?.lbname ?? lbentry?.title,
          id,
          status: latest?.status,
          autosave,
        },
        exclusives,
        yachtName: action.payload?.data?.yachtname,
        yachtData: {
          ...action.payload?.data?.content?.data?.yachtdata,
          yachtname: action.payload.data.yachtname,
        },
        cabins,
        isRestricted:
          action.payload.data.content?.data?.latest?.lbentry?.restricted ??
          action.payload.data.content.data.restricted,
        draft: action.payload.data.content.data.draft,
        saveReason: ESaveEntryReason.none,
        shared: latest?.lbentry?.shared ?? [],
        flags: latest?.flags,
      };
    },
    /**
     * Minimal state setup for V2 generators (avoids unnecessary V1 processing)
     * Only sets fields required by useGeneratorV2Saving and related hooks
     */
    setLogbookDataMinimalV2: (state, action) => {
      const logbooksForSelect: any[] = mapGeneratorLogbooks(
        action.payload?.data?.content?.data?.logbooks ?? [],
      );
      const entriesForSelect = mapLogbookEntriesForSelect(
        action.payload.data.content.data.entries ?? [],
      );
      const latest = action.payload?.data?.content?.data?.latest;
      const lbentry = latest?.lbentry ?? {};
      const id = resolveEntryId(latest);
      const files = latest?.attachments ?? [];
      const autosave =
        entriesForSelect.find((e: any) => e.value === id)?.autosave ?? 0;

      return {
        ...state,
        files,
        lbentry: {
          ...lbentry,
          isReadonly: action.payload.data.content?.data?.isReadonly ?? false,
          unCodeEnabled:
            action.payload.data.content?.data?.unCodeEnabled ?? false,
          sections: [],
          checklists: [],
        },
        entryDataToSave: {
          datecreated: latest?.datecreated,
          datecreatedUTC: latest?.datecreatedUTC,
          entrydate: null,
          latest: resolveEntryId(latest),
          lbid: latest?.lbid,
          yachtid: latest?.yachtid,
          _prename: latest?.lbname,
        },
        latestEntry: {
          entryref: latest?.enum,
          lbName: latest?.lbname ?? lbentry?.title,
          id,
          status: latest?.status,
          autosave,
        },
        yachtName: action.payload?.data?.yachtname,
        yachtData: {
          ...action.payload?.data?.content?.data?.yachtdata,
          yachtname: action.payload.data.yachtname,
        },
        success: action.payload.success,
        loading: false,
        logbooks: logbooksForSelect,
        entries: entriesForSelect,
        users: action.payload.data.content.data.users,
        saveReason: ESaveEntryReason.none,
      };
    },
    setSectionProperty: (
      state,
      action: PayloadAction<{ sectionIndex: number; property: any }>,
    ) => {
      const { sectionIndex, property } = action.payload;
      if (!state.lbentry.sections[sectionIndex]) {
        return;
      }
      state.lbentry.sections[sectionIndex] = {
        ...state.lbentry.sections[sectionIndex],
        ...property,
      };
    },
    setRowProperty: (
      state,
      action: PayloadAction<{
        sectionIndex: number;
        rowIndex: number;
        property: { [key: string]: any };
      }>,
    ) => {
      const { sectionIndex, rowIndex, property } = action.payload;
      if (
        !state.lbentry.sections[sectionIndex] ||
        !state.lbentry.sections[sectionIndex].rows[rowIndex]
      ) {
        return;
      }
      state.lbentry.sections[sectionIndex].rows[rowIndex] = {
        ...state.lbentry.sections[sectionIndex].rows[rowIndex],
        ...property,
      };
    },
    setCellProperty: (
      state,
      action: PayloadAction<{
        sectionIndex: number | undefined;
        rowIndex: number | undefined;
        cellId: string;
        rowUuid?: string;
        subUuid?: string;
        change?: string;
        property: Record<string, any>;
      }>,
    ) => {
      const {
        sectionIndex,
        rowIndex,
        cellId,
        rowUuid,
        subUuid,
        change,
        property,
      } = action.payload;
      if (
        typeof sectionIndex !== "number" ||
        typeof rowIndex !== "number" ||
        !state.lbentry.sections[sectionIndex]?.rows[rowIndex]
      ) {
        return;
      }
      const row = state.lbentry.sections[sectionIndex].rows[rowIndex] as any;
      if (!row) return;
      if (row.subrows && Array.isArray(row.subrows)) {
        if (typeof rowUuid === "string") {
          // placeholder for subrow logic
        }
      } else if (typeof subUuid === "string" && subUuid !== "") {
        if (change) {
          if (
            row.cols[subUuid].content.cols[cellId]?.[change] ===
            property[change]
          ) {
            return;
          }
        }
        row.cols[subUuid].content.cols[cellId] = {
          ...row.cols[subUuid].content.cols[cellId],
          ...property,
        };
      } else if (row.cols?.[cellId]) {
        if (change) {
          if (row.cols[cellId]?.[change] === property[change]) {
            return;
          }
        }

        row.cols[cellId] = {
          ...row.cols[cellId],
          ...property,
        };
      }

      state.lbentry.sections[sectionIndex].rows[rowIndex] = row;
    },
    updateUserContactNumber: (
      state,
      action: PayloadAction<{ userid: string; contactNumber: string }>,
    ) => {
      const { userid, contactNumber } = action.payload;
      const userIndex = state.users.findIndex(user => user.userid === userid);
      if (userIndex !== -1) {
        const user = state.users[userIndex];
        // Update the field that currently contains the contact number, or mobile if both are empty
        if (user.mobile) {
          // If user has mobile, update mobile
          state.users[userIndex] = {
            ...state.users[userIndex],
            mobile: contactNumber,
          };
        } else if (user.phoneno) {
          // If user has phoneno but no mobile, update phoneno
          state.users[userIndex] = {
            ...state.users[userIndex],
            phoneno: contactNumber,
          };
        } else {
          // If user has neither, default to mobile
          state.users[userIndex] = {
            ...state.users[userIndex],
            mobile: contactNumber,
          };
        }
      }
    },
    addDynamicColumn: (
      state,
      action: PayloadAction<{
        sectionIndex: number;
        columnIndex: number;
        headerIndex: number;
      }>,
    ) => {
      const { sectionIndex, columnIndex, headerIndex } = action.payload;
      const section = state.lbentry.sections[sectionIndex];

      if (!section) return;

      // Find the header to duplicate
      const headerToDuplicate = section.headers[headerIndex];
      if (!headerToDuplicate || !headerToDuplicate.dynamicCols) return;

      // Create a deep copy of the header
      const newHeader = JSON.parse(JSON.stringify(headerToDuplicate));

      // Insert the new header after the current one
      section.headers.splice(headerIndex + 1, 0, newHeader);

      // Trigger save mechanisms
      state.triggerTotalsUpdate = Math.random();
      state.triggerInlineSave = Math.random();

      // Update all rows to add a new column
      section.rows.forEach((row: any) => {
        if (row.subrows) {
          row.subrows.forEach((subrow: any) => {
            if (subrow.cols) {
              const colKeys = Object.keys(subrow.cols);
              const colToDuplicate = subrow.cols[colKeys[columnIndex]];
              if (colToDuplicate) {
                const newCol = JSON.parse(JSON.stringify(colToDuplicate));
                newCol.value = "";
                const newColId = uuidv4();

                // Insert the new column after the current one
                const newCols: any = {};
                Object.entries(subrow.cols).forEach(([key, col], index) => {
                  newCols[key] = col;
                  if (index === columnIndex) {
                    newCols[newColId] = newCol;
                  }
                });
                subrow.cols = newCols;
              }
            }
          });
        } else if (row.cols) {
          const colKeys = Object.keys(row.cols);
          const colToDuplicate = row.cols[colKeys[columnIndex]];
          if (colToDuplicate) {
            const newCol = JSON.parse(JSON.stringify(colToDuplicate));
            newCol.value = "";
            const newColId = uuidv4();

            const newCols: any = {};
            Object.entries(row.cols).forEach(([key, col], index) => {
              newCols[key] = col;
              if (index === columnIndex) {
                newCols[newColId] = newCol;
              }
            });
            row.cols = newCols;
          }
        }
      });
    },
    removeDynamicColumn: (
      state,
      action: PayloadAction<{
        sectionIndex: number;
        columnIndex: number;
        headerIndex: number;
      }>,
    ) => {
      const { sectionIndex, columnIndex, headerIndex } = action.payload;
      const section = state.lbentry.sections[sectionIndex];

      if (!section) return;

      // Check if this header can be removed (must be dynamic and not the last one)
      const headerToRemove = section.headers[headerIndex];
      if (!headerToRemove || !headerToRemove.dynamicCols) return;

      // Count how many dynamic columns exist in the same name-group
      // Grouping by name
      const dynamicColsCount = section.headers.filter(
        h => h.dynamicCols && h.name === headerToRemove.name,
      ).length;

      // Don't allow removal if it's the only column of this type
      if (dynamicColsCount <= 1) return;

      // Remove the header
      section.headers.splice(headerIndex, 1);

      // Trigger save mechanisms
      state.triggerTotalsUpdate = Math.random();
      state.triggerInlineSave = Math.random();

      // Remove the corresponding column from all rows
      section.rows.forEach((row: any) => {
        if (row.subrows) {
          row.subrows.forEach((subrow: any) => {
            if (subrow.cols) {
              const colKeys = Object.keys(subrow.cols);
              const colKeyToRemove = colKeys[columnIndex];
              if (colKeyToRemove && subrow.cols[colKeyToRemove]) {
                delete subrow.cols[colKeyToRemove];
              }
            }
          });
        } else if (row.cols) {
          const colKeys = Object.keys(row.cols);
          const colKeyToRemove = colKeys[columnIndex];
          if (colKeyToRemove && row.cols[colKeyToRemove]) {
            delete row.cols[colKeyToRemove];
          }
        }
      });
    },
    toggleSectionCollapse: (
      state,
      action: PayloadAction<{ sectionIndex: number }>,
    ) => {
      const { sectionIndex } = action.payload;
      const section = state.lbentry.sections[sectionIndex];
      if (section && section.collapseable) {
        section.collapsed = !section.collapsed;
      }
    },
    setSectionCollapsed: (
      state,
      action: PayloadAction<{ sectionIndex: number; collapsed: boolean }>,
    ) => {
      const { sectionIndex, collapsed } = action.payload;
      const section = state.lbentry.sections[sectionIndex];
      if (section && section.collapseable) {
        section.collapsed = collapsed;
      }
    },
    toggleAllCollapseable: state => {
      const allCollapsed = state.lbentry.sections
        .filter(section => section.collapseable)
        .every(section => section.collapsed);

      state.lbentry.sections.forEach(section => {
        if (section.collapseable) {
          section.collapsed = !allCollapsed;
        }
      });
    },
    setPrevEntryData: (state, action: PayloadAction<Record<string, any>>) => {
      state.lbentry.prevEntryData = {
        ...state.lbentry?.prevEntryData,
        ...action.payload,
      };
    },
    clearPrevEntryData: state => {
      state.lbentry.prevEntryData = {};
    },

    setPreviousVoyage: (
      state,
      action: PayloadAction<Record<string, any> | undefined>,
    ) => {
      state.previousVoyage = action.payload;
    },
    setVoyageByIndex: (
      state,
      action: PayloadAction<{
        rowIndex: number;
        start?: string | null;
        end?: string | null;
      }>,
    ) => {
      const { rowIndex, start, end } = action.payload;
      if (!state.voyages[rowIndex]) {
        state.voyages[rowIndex] = { start: null, end: null };
      }
      if (start !== undefined) {
        state.voyages[rowIndex].start = start;
      }
      if (end !== undefined) {
        state.voyages[rowIndex].end = end;
      }
    },
    clearVoyages: state => {
      state.voyages = {};
      state.previousVoyage = undefined;
    },
    removeVoyageByIndex: (state, action: PayloadAction<number>) => {
      const rowIndex = action.payload;
      const newVoyages: Record<
        number,
        { start: string | null; end: string | null }
      > = {};

      let newIndex = 0;
      Object.keys(state.voyages).forEach(key => {
        const idx = parseInt(key);
        if (idx !== rowIndex) {
          newVoyages[newIndex] = state.voyages[idx];
          newIndex++;
        }
      });

      state.voyages = newVoyages;
    },
    setSnapshotData: (
      state,
      action: PayloadAction<{
        snapshotEntry?: any;
        snapshotHeaders?: any[];
        flags?: string;
      }>,
    ) => {
      const { snapshotEntry, snapshotHeaders, flags } = action.payload;
      if (snapshotEntry !== undefined) {
        state.snapshotEntry = snapshotEntry;
      }
      if (snapshotHeaders !== undefined) {
        state.snapshotHeaders = snapshotHeaders;
      }
      if (flags !== undefined) {
        state.flags = flags;
      }
    },
    setSnapshotPageSize: (state, action: PayloadAction<number>) => {
      state.snapshotPageSize = action.payload;
    },
  },
});

export const {
  setSendDataOnNewEntry,
  refetchLogbook,
  resetState,
  setLoading,
  setSuccess,
  addNewDynamicRow,
  removeDynamicRow,
  setLogbook,
  setTimeZone,
  changeTimeZone,
  setExlusives,
  setCurrentMeta,
  setNewEntryData,
  setFiles,
  removeFile,
  setYachtName,
  setCurrentSignature,
  setShipType,
  setLogbookData,
  setLogbookDataMinimalV2,
  setIsRestricted,
  setShowAutoSaveModal,
  setLock24,
  setLatestEntryStatus,
  setLatestEntryId,
  setEntryDate,
  setCheckListsModalProps,
  setCheckList,
  setInitiallyOpen,
  reorderRows,
  setFeature,
  setStampsModalProps,
  setCheckLists,
  toggleHorizontalScrolls,
  setIsInitialInsert,
  setSectionProperty,
  setRowProperty,
  setCellProperty,
  setSaveReason,
  setTriggerInlineSave,
  setBridgeLogUsers,
  setPersistInlineSave,
  setForceTurnOffInlineSave,
  setLandscapeSection,
  addDynamicSection,
  removeDynamicSection,
  setSaveLockAvailable,
  setOowFeature,
  setEntryLockingDate,
  setDeisolate,
  setTriggerTotalsUpdate,
  setSections,
  sortSectionsByTime,
  addAutomationId,
  setNMEAData,
  toggleSortOrder,
  updateUserContactNumber,
  addDynamicColumn,
  removeDynamicColumn,
  toggleSectionCollapse,
  setSectionCollapsed,
  toggleAllCollapseable,
  setPrevEntryData,
  clearPrevEntryData,
  setPreviousVoyage,
  setVoyageByIndex,
  clearVoyages,
  removeVoyageByIndex,
  setSnapshotData,
  setSnapshotPageSize,
  setSigningOverride,
} = generatorSlice.actions;

export default generatorSlice.reducer;
